/**
 * Position-based Verlet integration with distance constraints.
 *
 * Used by the soft-body and rope techniques. Verlet is chosen over a force-based
 * integrator because constraints are satisfied by moving points directly, which is
 * unconditionally stable: a stiff spring cannot explode the way an explicit Euler
 * spring does at the same stiffness. Velocity is implicit in the difference between the
 * current and previous position, so there is no separate velocity array to keep in sync.
 *
 * Structure-of-arrays throughout; a letter mesh is a few thousand points and the
 * constraint solver touches all of them several times per step.
 */
export interface VerletSystem {
  x: Float32Array;
  y: Float32Array;
  px: Float32Array;
  py: Float32Array;
  /** 0 = pinned, otherwise inverse mass. */
  inverseMass: Float32Array;
  count: number;

  /** Constraint endpoints and rest lengths. */
  ca: Uint32Array;
  cb: Uint32Array;
  rest: Float32Array;
  stiffness: Float32Array;
  constraintCount: number;
}

export function createSystem(pointCapacity: number, constraintCapacity: number): VerletSystem {
  return {
    x: new Float32Array(pointCapacity),
    y: new Float32Array(pointCapacity),
    px: new Float32Array(pointCapacity),
    py: new Float32Array(pointCapacity),
    inverseMass: new Float32Array(pointCapacity),
    count: 0,
    ca: new Uint32Array(constraintCapacity),
    cb: new Uint32Array(constraintCapacity),
    rest: new Float32Array(constraintCapacity),
    stiffness: new Float32Array(constraintCapacity),
    constraintCount: 0,
  };
}

export function addPoint(system: VerletSystem, x: number, y: number, inverseMass = 1): number {
  const id = system.count++;
  system.x[id] = x;
  system.y[id] = y;
  system.px[id] = x;
  system.py[id] = y;
  system.inverseMass[id] = inverseMass;
  return id;
}

export function addConstraint(system: VerletSystem, a: number, b: number, stiffness: number, rest?: number): void {
  if (system.constraintCount >= system.ca.length) return;
  const id = system.constraintCount++;
  system.ca[id] = a;
  system.cb[id] = b;
  system.rest[id] = rest ?? Math.hypot(system.x[b] - system.x[a], system.y[b] - system.y[a]);
  system.stiffness[id] = stiffness;
}

export interface IntegrateOptions {
  gravityX: number;
  gravityY: number;
  damping: number;
  windX: number;
  windY: number;
}

export function integrate(system: VerletSystem, options: IntegrateOptions): void {
  const { x, y, px, py, inverseMass, count } = system;
  const keep = 1 - options.damping;

  for (let i = 0; i < count; i++) {
    if (inverseMass[i] === 0) continue;
    const vx = (x[i] - px[i]) * keep;
    const vy = (y[i] - py[i]) * keep;
    px[i] = x[i];
    py[i] = y[i];
    x[i] += vx + options.gravityX + options.windX;
    y[i] += vy + options.gravityY + options.windY;
  }
}

/**
 * Gauss-Seidel constraint relaxation.
 *
 * Each iteration nudges every constrained pair towards its rest length; more iterations
 * mean a stiffer-feeling body. Applying them sequentially (rather than accumulating and
 * applying at the end) converges considerably faster for the same iteration count.
 */
export function solve(system: VerletSystem, iterations: number): void {
  const { x, y, inverseMass, ca, cb, rest, stiffness, constraintCount } = system;

  for (let iter = 0; iter < iterations; iter++) {
    for (let c = 0; c < constraintCount; c++) {
      const a = ca[c];
      const b = cb[c];
      const wa = inverseMass[a];
      const wb = inverseMass[b];
      const sum = wa + wb;
      if (sum === 0) continue;

      const dx = x[b] - x[a];
      const dy = y[b] - y[a];
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len < 1e-6) continue;

      const correction = ((len - rest[c]) / len) * stiffness[c];
      const cx = dx * correction;
      const cy = dy * correction;
      x[a] += cx * (wa / sum);
      y[a] += cy * (wa / sum);
      x[b] -= cx * (wb / sum);
      y[b] -= cy * (wb / sum);
    }
  }
}

/** Keeps points inside the canvas with a little energy loss on contact. */
export function constrainToBounds(system: VerletSystem, width: number, height: number, bounce = 0.4): void {
  const { x, y, px, py, inverseMass, count } = system;
  for (let i = 0; i < count; i++) {
    if (inverseMass[i] === 0) continue;
    if (x[i] < 0) {
      x[i] = 0;
      px[i] = x[i] + (px[i] - x[i]) * bounce;
    } else if (x[i] > width) {
      x[i] = width;
      px[i] = x[i] + (px[i] - x[i]) * bounce;
    }
    if (y[i] < 0) {
      y[i] = 0;
      py[i] = y[i] + (py[i] - y[i]) * bounce;
    } else if (y[i] > height) {
      y[i] = height;
      py[i] = y[i] + (py[i] - y[i]) * bounce;
    }
  }
}
